import { useTelemetry } from '@shared/contexts/TelemetryContext';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useEffect, useMemo, useRef } from 'react';

export const useWidgetLifecycleTelemetry = (widgetId: string, widgetName: string, widgetType: string, payload?: any) => {
  const { collectMetric } = useTelemetry();
  const widgetLoadStartTime = useRef(performance.now());
  const renderCount = useRef(0);
  const user = useUserContext();
  const userDetails = useMemo(() => ({ userId: user?.id, email: user?.email, fullName: user?.full_name }), [user]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (widgetLoadStartTime.current && renderCount.current === 1) {
        const mountTime = performance.now() - widgetLoadStartTime.current;
        collectMetric('widget_mount_time', mountTime, 'ms', { widgetName, widgetId, widgetType, hasPayload: !!payload }, userDetails, [
          'widget',
          'mounting',
        ]);
      }
    });

    if (payload) {
      collectMetric('widget_payload_size', JSON.stringify(payload).length, 'bytes', { widgetName, widgetId, widgetType }, userDetails, [
        'widget',
        'payload',
      ]);
    }
    return () => {
      const totalLifetime = performance.now() - widgetLoadStartTime.current;
      if (totalLifetime > 1000) {
        collectMetric('widget_lifetime', totalLifetime, 'ms', { widgetName, widgetId, widgetType }, userDetails, ['widget', 'lifetime']);
      }
    };
  }, []);

  useEffect(() => {
    renderCount.current++;
    if (renderCount.current > 1) {
      const rerenderStartTime = performance.now();
      requestAnimationFrame(() => {
        const renderTime = performance.now() - rerenderStartTime;
        collectMetric('widget_rerender_time', renderTime, 'ms', { widgetName, widgetId, widgetType }, userDetails, ['widget', 'rerender']);
      });
    }
    collectMetric('widget_render_count', renderCount.current, 'count', { widgetName, widgetId, widgetType }, userDetails, ['widget', 'rendering']);
  });
};
